import { Controller } from "@hotwired/stimulus";
import { Target, TypedController, Value } from "@vytant/stimulus-decorators";
import type { TurboFrame } from "~/types";

import ModalsController from "../form_view/modals_controller";

@TypedController
export default class extends Controller {
  @Target overlayTarget: HTMLDivElement;

  @Target drawerTarget: HTMLDivElement;
  @Target loadingTarget: HTMLDivElement;

  @Target titleTarget: HTMLDivElement;
  @Target editTarget: HTMLAnchorElement;
  @Target linkTarget: HTMLAnchorElement;
  @Target linkOldTarget: HTMLAnchorElement;
  @Target deleteButtonTarget: HTMLFormElement;

  @Target actionsTarget: HTMLDetailsElement;
  declare readonly hasActionsTarget: boolean;

  /** I.e. users / newsletter_subscriptions */
  @Value(String) readonly modelPathValue!: string;

  /** I.e. user / newsletter_subscription */
  @Value(String) readonly modelNameValue!: string;

  connect() {
    document.addEventListener("keydown", (event) => {
      if (!this.isSheditOpen()) {
        return;
      }

      // if any dialog is open, don't close shedit
      if (document.querySelectorAll("dialog[open]").length > 0) {
        return;
      }

      if (event.key === "Escape") {
        this.closeShedit();
      }
    });

    document.addEventListener("close-shedit", () => {
      this.closeShedit();
    });
  }

  openShedit(event: MouseEvent) {
    // skip if opening in new tab
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();

    // @ts-ignore
    const target = (event.target instanceof HTMLAnchorElement ? event.target : event.target?.closest("a")) as HTMLAnchorElement;
    const id = target?.dataset.id;
    if (!id) {
      return;
    }

    const titleTemplate = target.parentElement?.querySelector<HTMLTemplateElement>("template.shedit-header");
    if (titleTemplate) {
      this.titleTarget.innerHTML = "";
      this.titleTarget.appendChild(titleTemplate.content.cloneNode(true));
    } else {
      this.titleTarget.textContent = "Ups...";
    }

    this.frameTarget.dataset.id = id;
    this.frameTarget.classList.add("hidden");
    this.frameTarget.src = `/dashboard/${this.modelPathValue}/${id}`;

    if (this.hasActionsTarget && this.actionsFrame) {
      this.actionsTarget.classList.add("hidden");
      // TODO: add disabled and set this on page load so it's easier to reload
      this.actionsFrame.src = `/dashboard/${this.modelPathValue}/${id}/actions`;
      this.actionsFrame.loaded.then(() => {
        this.actionsTarget.classList.remove("hidden");
      });
    }

    this.loadingTarget.classList.remove("hidden");
    this.drawerTarget.classList.remove("hidden");

    // update URLs
    const tmpUrl = new URL(window.location.href);
    tmpUrl.searchParams.set("id", id);
    window.history.pushState(window.history.state, "", tmpUrl.toString());

    this.linkTarget.href = `/dashboard/${this.modelPathValue}/${id}`;
    this.linkOldTarget.href = `/admin/${this.modelPathValue}/${id}`;
    this.deleteButtonTarget.action = `/dashboard/${this.modelPathValue}/${id}?source=shedit`;
    ModalsController.setData(
      this.editTarget,
      {
        frameId: `form-${this.modelNameValue}-${id}`,
        remoteUrl: `/dashboard/${this.modelPathValue}/${id}/edit`,
        modalQueryParam: `edit-${this.modelNameValue}:${id}`,
        modalTitle: "Loading edit...",
      },
      `/dashboard/${this.modelPathValue}/${id}?modal=edit`,
    );

    // on load remove loader and show frame
    this.frameTarget.loaded.then(() => {
      this.frameTarget.classList.remove("hidden");
      this.loadingTarget.classList.add("hidden");
    });

    this.frameTarget.addEventListener("turbo:frame-missing", (event) => {
      event.preventDefault();

      this.frameTarget.innerHTML = "";
      this.frameTarget.appendChild(document.querySelector<HTMLTemplateElement>("#error-loading")!.content.cloneNode(true));
      this.frameTarget.classList.remove("hidden");
      this.loadingTarget.classList.add("hidden");
    });

    this.overlayTarget.classList.remove("hidden");
  }

  closeShedit() {
    this.drawerTarget.classList.add("hidden");

    const tmpUrl = new URL(window.location.href);
    [
      "id",
      "tab",
      "history_page",
      "history_per_page",

      // For user relations
      ...[
        "tickets_page",
        "tickets_per_page",
        "subscriptions_page",
        "subscriptions_per_page",
        "newsletter_subscriptions_page",
        "newsletter_subscriptions_per_page",
      ],
    ].forEach((param) => {
      tmpUrl.searchParams.delete(param);
    });

    window.history.pushState(window.history.state, "", tmpUrl.toString());

    this.overlayTarget.classList.add("hidden");
  }

  private isSheditOpen() {
    return !this.drawerTarget.classList.contains("hidden");
  }

  private get frameTarget() {
    return this.element.querySelector<TurboFrame>("#shedit-frame")!;
  }

  private get actionsFrame() {
    return this.actionsTarget.querySelector<TurboFrame>("#shedit-actions")!;
  }
}
